import type { AdminRole, AdminStatus, DeveloperStatus, OperatorStatus, AuditAction, AuditResource } from '@/types';

/**
 * 管理员角色显示名称映射
 */
export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: '超级管理员',
  operator: '运营管理员',
  auditor: '审计员',
};

/**
 * 管理员状态显示名称映射
 */
export const ADMIN_STATUS_LABELS: Record<AdminStatus, string> = {
  active: '正常',
  disabled: '禁用',
};

/**
 * 管理员状态对应的 Ant Design Tag 颜色
 */
export const ADMIN_STATUS_COLORS: Record<AdminStatus, string> = {
  active: 'green',
  disabled: 'red',
};

/**
 * 开发者状态显示名称映射
 */
export const DEVELOPER_STATUS_LABELS: Record<DeveloperStatus, string> = {
  active: '正常',
  disabled: '已禁用',
};

/**
 * 开发者状态对应的 Ant Design Tag 颜色
 */
export const DEVELOPER_STATUS_COLORS: Record<DeveloperStatus, string> = {
  active: 'green',
  disabled: 'volcano',
};

/**
 * 操作员状态显示名称映射
 */
export const OPERATOR_STATUS_LABELS: Record<OperatorStatus, string> = {
  active: '活跃',
  disabled: '已禁用',
};

/**
 * 操作员状态对应的 Ant Design Tag 颜色
 */
export const OPERATOR_STATUS_COLORS: Record<OperatorStatus, string> = {
  active: 'green',
  disabled: 'volcano',
};

/**
 * 审计日志动作类型显示名称映射
 */
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  login: '登录',
  logout: '登出',
  create: '创建',
  update: '更新',
  delete: '删除',
  disable: '禁用',
  enable: '启用',
  read: '查询',
  bind: '绑定',
  unbind: '解绑',
  reset_secret: '重置密钥',
  reset_password: '重置密码',  // ✅ 新增：重置密码动作映射
  send_verification_code: '发送验证码',
  send_reset_code: '发送重置码',
  register: '注册',
  send_delete_code: '发送注销码',
  delete_account: '注销账号',
};

/**
 * 审计日志动作类型对应的 Ant Design Tag 颜色
 */
export const AUDIT_ACTION_COLORS: Record<AuditAction, string> = {
  login: 'blue',
  logout: 'gray',
  create: 'cyan',
  update: 'orange',
  delete: 'red',
  disable: 'volcano',
  enable: 'green',
  read: 'default',
  bind: 'purple',
  unbind: 'gold',
  reset_secret: 'magenta',
  reset_password: 'geekblue',  // ✅ 新增：重置密码动作颜色
  send_verification_code: 'lime',
  send_reset_code: 'lime',
  register: 'green',
  send_delete_code: 'orange',
  delete_account: 'red',
};

/**
 * 审计日志资源类型显示名称映射（使用表名映射）
 */
export const AUDIT_RESOURCE_LABELS: Record<AuditResource, string> = {
  admin_auth: '管理员认证',
  developers: '开发者账号',
  operators: '操作员',
  products: '产品',
  app_instances: 'App SDK',
  apps: 'App SDK',
  panel_templates: '控制面板',
  panels: '控制面板',
  product_images: '产品图片',
  ota_firmwares: 'OTA 固件',
  panel_images: '面板图片',
  android_sha256_certificates: 'Android 证书',
  app_product_integrations: 'App 产品集成',
  product_categories: '产品类别',
  verification_codes: '验证码',
  captcha_sessions: '验证码会话',
  captcha_behaviors: '验证码行为',
  audit_logs: '审计日志',
  admins: '管理员',
  auth: '认证',
  logout: '登出操作',
  password: '密码',  // ✅ 新增：密码资源映射
  meta_monitor_alert_configs: '元监控告警配置', // ✅ 新增：元监控告警配置
};

/**
 * 前端路由路径常量
 */
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/',
  TENANTS: '/tenants',
  CATEGORIES: '/categories',
  OPERATORS: '/operators',
  AUDIT: '/audit',
} as const;

/**
 * LocalStorage Key 常量
 */
export const STORAGE_KEYS = {
  ADMIN_TOKEN: 'admin_token',
  ADMIN_USER: 'admin_user',
} as const;

/**
 * 默认分页大小
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * 分页选项
 */
export const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];